import {
  Button,
  ConfirmDialog,
  type ControlledSearch,
  DataTable,
  type LocalDataColumn,
  type TableColumnFilter,
  TOUCH,
} from "@nymbl/nos2-rules";
import { Copy, ListX, Pencil, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { TypeChip } from "@/components/time/TypeChip";
import { useDeleteEntry } from "@/hooks/use-time-entries";
import { TIME_ENTRIES_HELP } from "@/lib/column-help";
import { TIME_ENTRY_TYPE_OPTIONS } from "@/lib/enums";
import { withCells } from "@/lib/table-columns";
import { formatEntryDate } from "@/lib/time/dates";
import { entryActionLabel, entryLabelSuffixes, projectLine } from "@/lib/time/labels";
import { entryLock, formatHours, LOCK_LABELS } from "@/lib/time/rules";
import { cn } from "@/lib/utils";
import type { AssignableTask, TimeEntryView } from "@/schemas/time";

interface EntriesListProps {
  entries: TimeEntryView[];
  /** Entries carry no account; it comes from the person's assignment of the same id. */
  assignable: AssignableTask[];
  /** The week (default) or the whole month: names the table and the empty state. */
  period?: { kind: "week" } | { kind: "month"; label: string };
  /** The table's title line: the view switch and Favorites, shared with the Grid view. */
  title: string;
  controls: ReactNode;
  /** Opens filtered, when Entries came from a grid cell; remount with a `key` for new ones. */
  initialFilters?: Record<string, TableColumnFilter>;
  /** The search, shared with the Grid view. */
  searchState?: ControlledSearch;
  onCopy: (entry: TimeEntryView) => void;
  onEdit: (entry: TimeEntryView) => void;
}

/** The Entries table's columns, shared by the week and month views (column help test). */
export const ENTRIES_COLUMNS: LocalDataColumn<TimeEntryView>[] = [
  {
    key: "date",
    label: "Date",
    type: "date",
    accessor: (e) => e.date,
    help: TIME_ENTRIES_HELP.date,
  },
  {
    key: "task",
    label: "Task",
    type: "text",
    accessor: (e) => e.taskName,
    searchText: (e) => (e.isPto ? "Time off" : (e.projectName ?? "")),
    help: TIME_ENTRIES_HELP.task,
  },
  {
    key: "type",
    label: "Type",
    type: "enum",
    accessor: (e) => e.type,
    options: [...TIME_ENTRY_TYPE_OPTIONS],
    help: TIME_ENTRIES_HELP.type,
  },
  {
    key: "hours",
    label: "Hours",
    type: "number",
    accessor: (e) => e.hours,
    help: TIME_ENTRIES_HELP.hours,
  },
  {
    key: "comment",
    label: "Comment",
    type: "text",
    accessor: (e) => e.comment,
    help: TIME_ENTRIES_HELP.comment,
  },
];

// Same-day entries: newest first is the date column's own order; then a stable id.
const newestFirst = (a: TimeEntryView, b: TimeEntryView) =>
  b.date.localeCompare(a.date) || a.id.localeCompare(b.id);

interface EntryActionsProps {
  entry: TimeEntryView;
  /** " (1 of 2)" when another entry has the same task, day and hours. */
  suffix: string;
  onCopy: (entry: TimeEntryView) => void;
  onEdit: (entry: TimeEntryView) => void;
  onDelete: (entry: TimeEntryView) => void;
}

function EntryActions({ entry, suffix, onCopy, onEdit, onDelete }: EntryActionsProps) {
  const lock = entryLock(entry);
  return (
    <div className="flex items-center justify-end gap-1">
      {lock && <span className="text-xs text-muted-foreground">{LOCK_LABELS[lock]}</span>}
      {!entry.isPto && (
        <Button
          variant="ghost"
          size="icon"
          className={TOUCH}
          aria-label={entryActionLabel("Copy", entry, suffix)}
          onClick={() => onCopy(entry)}
        >
          <Copy aria-hidden="true" />
        </Button>
      )}
      {!lock && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className={TOUCH}
            aria-label={entryActionLabel("Edit", entry, suffix)}
            onClick={() => onEdit(entry)}
          >
            <Pencil aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={TOUCH}
            aria-label={entryActionLabel("Delete", entry, suffix)}
            onClick={() => onDelete(entry)}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        </>
      )}
    </div>
  );
}

function TaskCell({ entry, accountName }: { entry: TimeEntryView; accountName: string | null }) {
  return (
    <>
      <div className="font-semibold">{entry.taskName}</div>
      <div className="text-xs text-muted-foreground">
        {entry.isPto ? "Time off" : projectLine(entry.projectName, accountName)}
      </div>
    </>
  );
}

export function EntriesList({
  entries,
  assignable,
  period = { kind: "week" },
  title,
  controls,
  initialFilters,
  searchState,
  onCopy,
  onEdit,
}: EntriesListProps) {
  const remove = useDeleteEntry();
  const rows = useMemo(() => [...entries].sort(newestFirst), [entries]);
  // Numbered in the table's opening order, so "(1 of 2)" is the upper of two identical entries.
  const suffixes = useMemo(() => entryLabelSuffixes(rows), [rows]);
  const accounts = useMemo(
    () => new Map(assignable.map((a) => [a.demandPersonId.toLowerCase(), a.accountName])),
    [assignable],
  );
  const [toDelete, setToDelete] = useState<TimeEntryView | null>(null);
  const columns = useMemo(
    () =>
      withCells(ENTRIES_COLUMNS, {
        date: { render: (e) => formatEntryDate(e.date) },
        task: {
          render: (e) => (
            <TaskCell
              entry={e}
              accountName={accounts.get(e.demandPersonId?.toLowerCase() ?? "") ?? null}
            />
          ),
        },
        type: { render: (e) => <TypeChip type={e.type} /> },
        hours: {
          render: (e) => <span className="font-semibold">{formatHours(e.hours)}</span>,
        },
        comment: {
          className: "min-w-48 max-w-md break-words",
          render: (e) => (
            <span className={cn(!e.comment && "text-muted-foreground")}>{e.comment || "–"}</span>
          ),
        },
      }),
    [accounts],
  );

  return (
    <div className="space-y-3">
      <DataTable
        label={period.kind === "month" ? `Entries, ${period.label}` : "Entries"}
        title={title}
        search={{ placeholder: "Search entries…" }}
        exportName="time-entries"
        columns={columns}
        rows={rows}
        rowKey={(e) => e.id}
        defaultSort="date"
        defaultSortDir="desc"
        controls={controls}
        initialFilters={initialFilters}
        searchState={searchState}
        empty={{
          title: period.kind === "month" ? "No entries this month" : "No entries this week",
          icon: ListX,
        }}
        rowActions={(entry) => (
          <EntryActions
            entry={entry}
            suffix={suffixes.get(entry.id) ?? ""}
            onCopy={onCopy}
            onEdit={onEdit}
            onDelete={setToDelete}
          />
        )}
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title="Delete this entry?"
        description={
          toDelete
            ? `${toDelete.taskName} · ${formatEntryDate(toDelete.date)} · ${formatHours(toDelete.hours)} hrs`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        loading={remove.isPending}
        onConfirm={() => {
          if (!toDelete) return;
          remove.mutate(
            { id: toDelete.id, openedChangedOn: toDelete.changedOn },
            { onSuccess: () => setToDelete(null) },
          );
        }}
      />
    </div>
  );
}
